import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { adminApi, getAdminToken, setAdminToken, adminPhotoUrl, API } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Shield } from "lucide-react";

const TABS = ["Dashboard", "Matching", "Pending", "Verifications", "Users", "Payments", "Payment Settings", "Site Settings", "Reports", "Sessions", "Logs"];

export default function AdminPanel() {
  const [tab, setTab] = useState("Dashboard");
  const [data, setData] = useState({ dash: null, users: [], payments: [], payStats: null, settings: null, reports: [], sessions: [], logs: [], pending: [], verifications: [] });
  const [payFilter, setPayFilter] = useState("PENDING");
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const token = getAdminToken();

  const load = async (which) => {
    try {
      if (which === "Dashboard") { const { data: d } = await adminApi.get("/admin/dashboard"); setData((p) => ({ ...p, dash: d })); }
      if (which === "Pending") { const { data: d } = await adminApi.get("/admin/users?status_filter=PENDING"); setData((p) => ({ ...p, pending: d })); }
      if (which === "Verifications") { const { data: d } = await adminApi.get("/admin/verifications"); setData((p) => ({ ...p, verifications: d })); }
      if (which === "Users") { const { data: d } = await adminApi.get("/admin/users"); setData((p) => ({ ...p, users: d })); }
      if (which === "Payments") {
        const [{ data: d }, { data: s }] = await Promise.all([
          adminApi.get(`/admin/payments${payFilter ? `?status_filter=${payFilter}` : ""}`),
          adminApi.get("/admin/payments-stats"),
        ]);
        setData((p) => ({ ...p, payments: d, payStats: s }));
      }
      if (which === "Payment Settings") { const { data: s } = await adminApi.get("/admin/payment-settings"); setData((p) => ({ ...p, settings: s })); }
      if (which === "Site Settings") { const { data: s } = await adminApi.get("/admin/site-settings"); setData((p) => ({ ...p, site: s })); }
      if (which === "Matching") { const { data: m } = await adminApi.get("/admin/matching/overview"); setData((p) => ({ ...p, matching: m })); }
      if (which === "Reports") { const { data: d } = await adminApi.get("/admin/reports"); setData((p) => ({ ...p, reports: d })); }
      if (which === "Sessions") { const { data: d } = await adminApi.get("/admin/sessions"); setData((p) => ({ ...p, sessions: d })); }
      if (which === "Logs") { const { data: d } = await adminApi.get("/admin/logs"); setData((p) => ({ ...p, logs: d })); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };

  useEffect(() => { if (token) load(tab); }, [tab, token]);
  useEffect(() => { if (token && tab === "Payments") load("Payments"); }, [payFilter]);

  if (!token) return <Navigate to="/admin/login" replace />;

  const act = async (uid, action, body = {}) => {
    try {
      if (action === "delete") await adminApi.delete(`/admin/users/${uid}`);
      else await adminApi.post(`/admin/users/${uid}/${action}`, body);
      toast.success("Done");
      load(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const approvePay = async (pid) => {
    try { await adminApi.post(`/admin/payments/${pid}/approve`); toast.success("Payment approved · 24h session started"); load("Payments"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const openReject = (pid) => { setRejectModal(pid); setRejectReason(""); };
  const rejectPay = async () => {
    try {
      await adminApi.post(`/admin/payments/${rejectModal}/reject`, { reason: rejectReason });
      toast.success("Payment rejected");
      setRejectModal(null);
      load("Payments");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div className="min-h-screen bg-dc-gradient">
      <nav className="flex items-center justify-between p-4 max-w-6xl mx-auto">
        <Link to="/" className="font-display text-lg tracking-widest text-white">DILCONNECT · ADMIN</Link>
        <button onClick={() => { setAdminToken(null); window.location.href = "/admin/login"; }} className="text-sm text-purple-200 hover:text-white">Sign out</button>
      </nav>

      <div className="max-w-6xl mx-auto p-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {TABS.map((t) => (
            <button data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-xl text-sm whitespace-nowrap ${tab === t ? "bg-white text-purple-950 font-bold" : "bg-purple-900/40 text-purple-200"}`}>{t}</button>
          ))}
        </div>

        {tab === "Dashboard" && data.dash && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[
              ["Users total", data.dash.users.total],
              ["Pending", data.dash.users.pending],
              ["Approved", data.dash.users.approved],
              ["Rejected", data.dash.users.rejected],
              ["Blocked", data.dash.users.blocked],
              ["Suspended", data.dash.users.suspended],
              ["BOY", data.dash.counts.boy],
              ["GIRL", data.dash.counts.girl],
              ["Approved pay", data.dash.payments.paid],
              ["Pending pay", data.dash.payments.pending],
              ["Rejected pay", data.dash.payments.failed],
              ["Active sessions", data.dash.sessions.active],
              ["Expired sessions", data.dash.sessions.expired],
              ["Active matches", data.dash.matches.active],
              ["Open reports", data.dash.reports.open],
            ].map(([k, v]) => (
              <div key={k} className="glass rounded-2xl p-4">
                <div className="text-[11px] uppercase tracking-widest text-purple-200/70">{k}</div>
                <div className="font-display text-3xl font-black mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "Payments" && (
          <div className="mt-4">
            <div className="flex items-center gap-2 flex-wrap">
              {["PENDING", "APPROVED", "REJECTED", ""].map((s) => (
                <button key={s || "ALL"} data-testid={`pay-filter-${(s || "all").toLowerCase()}`} onClick={() => setPayFilter(s)} className={`px-3 py-1.5 rounded-xl text-xs font-bold ${payFilter === s ? "bg-white text-purple-950" : "bg-purple-900/40 text-purple-200"}`}>
                  {s || "All"} {data.payStats && (s === "PENDING" ? `(${data.payStats.pending})` : s === "APPROVED" ? `(${data.payStats.approved})` : s === "REJECTED" ? `(${data.payStats.rejected})` : `(${data.payStats.total})`)}
                </button>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.payments.length === 0 && <div className="text-purple-200/70 text-sm">No payments.</div>}
              {data.payments.map((p) => (
                <div key={p.id} data-testid={`pay-row-${p.id}`} className="glass rounded-3xl p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-mono text-xs text-purple-200/80">{p.verification_id || p.id.slice(0, 8)}</div>
                      <div className="font-bold text-base">{p.name} <span className="text-xs font-normal text-purple-200/70">· {p.user_display_name}</span></div>
                      <div className="text-xs text-purple-200/70">{p.user_email} · {p.mobile}</div>
                    </div>
                    <div className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                      p.status === "APPROVED" ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/40" :
                      p.status === "REJECTED" ? "bg-rose-500/20 text-rose-200 border border-rose-400/40" :
                      "bg-amber-500/20 text-amber-200 border border-amber-400/40"
                    }`}>{p.status}</div>
                  </div>
                  <div className="mt-3 text-sm">
                    <div><span className="text-purple-200/70">Amount:</span> <b>₹{(p.amount / 100).toFixed(0)}</b></div>
                    <div className="font-mono text-xs mt-0.5"><span className="text-purple-200/70">UTR:</span> {p.utr}</div>
                    <div className="text-[11px] text-purple-200/60 mt-0.5">Submitted: {new Date(p.submitted_at).toLocaleString()}</div>
                    {p.approved_at && <div className="text-[11px] text-emerald-300 mt-0.5">Approved: {new Date(p.approved_at).toLocaleString()}</div>}
                    {p.rejected_at && <div className="text-[11px] text-rose-300 mt-0.5">Rejected: {new Date(p.rejected_at).toLocaleString()}</div>}
                    {p.rejection_reason && <div className="text-[11px] text-rose-200/80 mt-0.5">Reason: {p.rejection_reason}</div>}
                  </div>
                  {p.screenshot_photo_id && (
                    <a href={adminPhotoUrl(p.screenshot_photo_id)} target="_blank" rel="noreferrer" className="block mt-3">
                      <img src={adminPhotoUrl(p.screenshot_photo_id)} className="w-full max-h-56 object-contain rounded-xl bg-black/40" alt="Payment screenshot" />
                    </a>
                  )}
                  {p.status === "PENDING" && (
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`pay-approve-${p.id}`} onClick={() => approvePay(p.id)} className="flex-1 px-3 py-2 rounded-xl bg-emerald-500/80 text-white text-sm font-bold inline-flex items-center justify-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                      <button data-testid={`pay-reject-${p.id}`} onClick={() => openReject(p.id)} className="flex-1 px-3 py-2 rounded-xl bg-rose-500/80 text-white text-sm font-bold inline-flex items-center justify-center gap-1">
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Site Settings" && (
          <SiteSettingsTab site={data.site} reload={() => load("Site Settings")} />
        )}

        {tab === "Matching" && (
          <MatchingTab overview={data.matching} reload={() => load("Matching")} />
        )}

        {tab === "Payment Settings" && (
          <PaymentSettingsTab settings={data.settings} reload={() => load("Payment Settings")} />
        )}

        {tab === "Pending" && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.pending.length === 0 && <div className="text-purple-200/70 text-sm">No pending users.</div>}
            {data.pending.map((u) => (
              <div key={u.id} className="glass rounded-3xl p-4">
                <div className="flex gap-2">
                  {(u.photo_ids || []).slice(0, 3).map((pid) => <img key={pid} src={adminPhotoUrl(pid)} className="w-20 h-20 object-cover rounded-xl" alt="" />)}
                </div>
                <div className="mt-3">
                  <div className="font-bold">{u.display_name} <span className="text-xs text-purple-200/70">· {u.age} · {u.gender} · {u.city}</span></div>
                  <div className="text-xs text-purple-200/70">{u.email}</div>
                  <div className="text-xs text-purple-200/70 mt-1">{u.bio}</div>
                  {(u.languages?.length || u.preferred_languages?.length) ? (
                    <div className="text-[11px] text-purple-200/70 mt-2">
                      <b>Languages:</b> {(u.languages || []).join(", ") || "—"}<br/>
                      <b>Preferred:</b> {(u.preferred_languages || []).join(", ") || "—"}
                    </div>
                  ) : null}
                  {(u.interests?.length || u.preferred_interests?.length) ? (
                    <div className="text-[11px] text-purple-200/70 mt-1">
                      <b>Interests:</b> {(u.interests || []).join(", ") || "—"}<br/>
                      <b>Preferred:</b> {(u.preferred_interests || []).join(", ") || "—"}
                    </div>
                  ) : null}
                </div>
                <div className="mt-3 flex gap-2 text-sm">
                  <button data-testid={`approve-${u.id}`} onClick={() => act(u.id, "approve")} className="px-3 py-1.5 rounded-xl bg-emerald-500/80 text-white">Approve</button>
                  <button data-testid={`reject-${u.id}`} onClick={() => act(u.id, "reject", { reason: "Review failed" })} className="px-3 py-1.5 rounded-xl bg-rose-500/80">Reject</button>
                  <button onClick={() => act(u.id, "block")} className="px-3 py-1.5 rounded-xl bg-slate-700">Block</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Verifications" && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.verifications.length === 0 && <div className="text-purple-200/70 text-sm">No pending verifications.</div>}
            {data.verifications.map((u) => (
              <div key={u.id} className="glass rounded-3xl p-4">
                <div className="flex gap-2 flex-wrap">
                  {u.verification_photo_id && <img src={adminPhotoUrl(u.verification_photo_id)} className="w-28 h-28 object-cover rounded-xl border-2 border-sky-400/50" alt="Selfie" />}
                  {(u.photo_ids || []).slice(0, 2).map((pid) => <img key={pid} src={adminPhotoUrl(pid)} className="w-20 h-20 object-cover rounded-xl" alt="" />)}
                </div>
                <div className="mt-3">
                  <div className="font-bold">{u.display_name} <span className="text-xs text-purple-200/70">· {u.age} · {u.gender} · {u.city}</span></div>
                  <div className="text-xs text-purple-200/70">{u.email}</div>
                </div>
                <div className="mt-3 flex gap-2 text-sm">
                  <button data-testid={`verify-${u.id}`} onClick={() => act(u.id, "verify")} className="px-3 py-1.5 rounded-xl bg-sky-500/80 text-white">Grant Verified</button>
                  <button data-testid={`unverify-${u.id}`} onClick={() => act(u.id, "unverify", { reason: "Selfie didn't match prompt" })} className="px-3 py-1.5 rounded-xl bg-rose-500/80">Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Users" && (
          <div className="mt-4 glass rounded-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-purple-900/40 text-purple-200">
                <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="p-3">Gender</th><th className="p-3">Age</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.id} className="border-t border-purple-500/10">
                    <td className="p-3">{u.display_name}{u.verified ? " ✓" : ""}</td>
                    <td className="p-3 text-purple-200/80">{u.email}</td>
                    <td className="p-3 text-center">{u.gender}</td>
                    <td className="p-3 text-center">{u.age}</td>
                    <td className="p-3 text-center">{u.status}</td>
                    <td className="p-3 space-x-1">
                      <button onClick={() => act(u.id, "approve")} className="text-emerald-300 text-xs">Approve</button>
                      <button onClick={() => act(u.id, "block")} className="text-rose-300 text-xs">Block</button>
                      <button onClick={() => act(u.id, "unblock")} className="text-sky-300 text-xs">Unblock</button>
                      <button onClick={() => act(u.id, "suspend")} className="text-amber-300 text-xs">Suspend</button>
                      <button onClick={() => act(u.id, "delete")} className="text-slate-300 text-xs">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Reports" && (
          <div className="mt-4 space-y-2">
            {data.reports.map((r) => (
              <div key={r.id} className="glass rounded-2xl p-4">
                <div className="text-sm"><b>{r.reason}</b> · reporter {r.reporter_id.slice(0,8)} → reported {r.reported_id.slice(0,8)}</div>
                <div className="text-xs text-purple-200/70 mt-1">{r.description}</div>
                <div className="text-[10px] text-purple-200/50 mt-1">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "Sessions" && (
          <div className="mt-4 glass rounded-3xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-purple-900/40 text-purple-200"><tr><th className="p-3">Session</th><th className="p-3">User</th><th className="p-3">Status</th><th className="p-3">Started</th><th className="p-3">Expires</th></tr></thead>
              <tbody>
                {data.sessions.map((s) => (
                  <tr key={s.id} className="border-t border-purple-500/10 text-center">
                    <td className="p-3 font-mono text-xs">{s.id.slice(0,8)}</td>
                    <td className="p-3 font-mono text-xs">{s.user_id.slice(0,8)}</td>
                    <td className="p-3">{s.status}</td>
                    <td className="p-3 text-xs">{new Date(s.started_at).toLocaleString()}</td>
                    <td className="p-3 text-xs">{new Date(s.expires_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Logs" && (
          <div className="mt-4 space-y-2">
            {data.logs.map((l) => (
              <div key={l.id} className="glass rounded-2xl p-3 text-sm flex items-center justify-between">
                <div><b>{l.action}</b> → <span className="font-mono text-xs">{l.target.slice(0,8)}</span></div>
                <div className="text-xs text-purple-200/70">{new Date(l.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {rejectModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="glass-strong rounded-3xl p-6 max-w-sm w-full">
            <h3 className="font-display text-xl font-bold">Reject payment</h3>
            <textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (optional)" rows={3} className="mt-3 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-white outline-none" />
            <div className="mt-4 flex gap-2">
              <button onClick={() => setRejectModal(null)} className="flex-1 py-2.5 rounded-xl bg-purple-900/60">Cancel</button>
              <button data-testid="reject-confirm" onClick={rejectPay} className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white font-bold">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SiteSettingsTab({ site, reload }) {
  const [boys, setBoys] = useState(0);
  const [girls, setGirls] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (site) { setBoys(site.base_boy_count); setGirls(site.base_girl_count); } }, [site]);
  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/admin/site-settings", { base_boy_count: Number(boys), base_girl_count: Number(girls) });
      toast.success("Site settings saved");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!site) return <div className="mt-6 text-purple-200/70">Loading…</div>;
  return (
    <div className="mt-4 glass-strong rounded-3xl p-6 max-w-xl">
      <h2 className="font-display text-xl font-bold">Homepage Counter Floor</h2>
      <p className="text-xs text-purple-200/70 mt-1">Public BOYS/GIRLS counters show <b>base + real approved users</b>. These bases are seeded with 1827/1672 and editable here.</p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-purple-200/70">Base BOYS</span>
          <input data-testid="site-base-boys" type="number" min={0} value={boys} onChange={(e) => setBoys(e.target.value)} className="mt-1 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-white outline-none" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-purple-200/70">Base GIRLS</span>
          <input data-testid="site-base-girls" type="number" min={0} value={girls} onChange={(e) => setGirls(e.target.value)} className="mt-1 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-white outline-none" />
        </label>
      </div>
      <button data-testid="site-save" disabled={busy} onClick={save} className="btn-cta mt-4 py-2.5 px-5 rounded-xl">{busy ? "Saving…" : "Save floor"}</button>
    </div>
  );
}

function MatchingTab({ overview, reload }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [filter, setFilter] = useState({ q: "", gender: "" });
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    if (!a || !b) { toast.error("Select two users"); return; }
    if (!window.confirm("Are you sure you want to manually connect these users?")) return;
    setBusy(true);
    try { await adminApi.post("/admin/matching/connect", { user_a_id: a, user_b_id: b }); toast.success("Users connected"); setA(""); setB(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const applyFilter = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.q) params.set("q", filter.q);
      if (filter.gender) params.set("gender", filter.gender);
      const { data } = await adminApi.get(`/admin/matching/overview?${params.toString()}`);
      // reuse parent state via a mini reload
      overview.stats = data.stats; overview.connections = data.connections; overview.not_connected = data.not_connected;
      reload();
    } catch (e) { toast.error("Load failed"); }
  };
  if (!overview) return <div className="mt-6 text-purple-200/70">Loading…</div>;
  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["Approved total", overview.stats.approved_total],
          ["Connected", overview.stats.connected],
          ["Not connected", overview.stats.not_connected],
          ["Active sessions", overview.stats.sessions_active],
          ["Automatic conns", overview.stats.connections_automatic],
          ["Manual conns", overview.stats.connections_manual],
        ].map(([k, v]) => (
          <div key={k} className="glass rounded-2xl p-4">
            <div className="text-[11px] uppercase tracking-widest text-purple-200/70">{k}</div>
            <div className="font-display text-2xl font-black mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="glass-strong rounded-3xl p-5">
        <h3 className="font-display text-lg font-bold">Manual Connect</h3>
        <p className="text-xs text-purple-200/70 mt-1">Admin override — creates an ADMIN_MANUAL connection, stops active matching timers, reveals approved profiles, enables chat.</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select data-testid="mm-user-a" value={a} onChange={(e) => setA(e.target.value)} className="bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-white">
            <option value="">User A…</option>
            {overview.not_connected.map((u) => (<option key={u.id} value={u.id}>{u.display_name} · {u.gender} · {u.age} · {u.city}</option>))}
          </select>
          <select data-testid="mm-user-b" value={b} onChange={(e) => setB(e.target.value)} className="bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-white">
            <option value="">User B…</option>
            {overview.not_connected.filter((u) => u.id !== a).map((u) => (<option key={u.id} value={u.id}>{u.display_name} · {u.gender} · {u.age} · {u.city}</option>))}
          </select>
        </div>
        <button data-testid="mm-connect" disabled={busy || !a || !b} onClick={connect} className="btn-cta mt-3 py-2.5 px-5 rounded-xl disabled:opacity-50">{busy ? "Connecting…" : "Manually Connect"}</button>
      </div>

      <div className="glass rounded-3xl p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="font-display text-lg font-bold">Active Connections ({overview.connections.length})</h3>
        </div>
        {overview.connections.length === 0 && <div className="text-sm text-purple-200/70 mt-2">No active connections.</div>}
        <ul className="mt-3 space-y-2">
          {overview.connections.map((c) => (
            <li key={c.connection_id} className="flex items-center justify-between bg-black/30 rounded-xl px-3 py-2 gap-2">
              <div className="text-sm">
                <b>{c.user_a.display_name}</b> ↔ <b>{c.user_b.display_name}</b>
                <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-purple-900/60 border border-purple-500/30">{c.source}</span>
              </div>
              <div className="text-[11px] text-purple-200/60 font-mono">{c.connection_id.slice(0, 8)}</div>
            </li>
          ))}
        </ul>
      </div>

      <div className="glass rounded-3xl p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="font-display text-lg font-bold">Not Yet Connected ({overview.not_connected.length})</h3>
          <div className="flex gap-2 items-center">
            <input data-testid="mm-search" placeholder="Search name/email" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="bg-black/40 border border-purple-500/30 rounded-xl px-3 py-1.5 text-xs text-white" />
            <select data-testid="mm-gender" value={filter.gender} onChange={(e) => setFilter({ ...filter, gender: e.target.value })} className="bg-black/40 border border-purple-500/30 rounded-xl px-2 py-1.5 text-xs text-white">
              <option value="">All</option><option value="male">Boys</option><option value="female">Girls</option>
            </select>
            <button onClick={applyFilter} className="px-3 py-1.5 rounded-xl bg-fuchsia-600 text-xs font-bold">Filter</button>
          </div>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-purple-200 text-left">
              <tr><th className="p-2">Name</th><th className="p-2">Gender</th><th className="p-2">Age</th><th className="p-2">City</th><th className="p-2">Session</th><th className="p-2">Payment</th></tr>
            </thead>
            <tbody>
              {overview.not_connected.map((u) => (
                <tr key={u.id} className="border-t border-purple-500/10">
                  <td className="p-2">{u.display_name}{u.verified ? " ✓" : ""}</td>
                  <td className="p-2">{u.gender}</td>
                  <td className="p-2">{u.age}</td>
                  <td className="p-2">{u.city}</td>
                  <td className="p-2 text-xs">{u.session_active ? <span className="text-emerald-300">ACTIVE</span> : <span className="text-purple-200/60">—</span>}</td>
                  <td className="p-2 text-xs">{u.payment_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PaymentSettingsTab({ settings, reload }) {
  const [upi, setUpi] = useState("");
  const [amount, setAmount] = useState(199);
  const [instructions, setInstructions] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [qrFile, setQrFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setUpi(settings.upi_id || "");
    setAmount(Math.round((settings.amount_paise || 19900) / 100));
    setInstructions(settings.instructions || "");
    setEnabled(!!settings.enabled);
  }, [settings]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/admin/payment-settings", { upi_id: upi.trim(), amount_inr: Number(amount), instructions, enabled });
      if (qrFile) {
        const fd = new FormData();
        fd.append("file", qrFile);
        await adminApi.post("/admin/payment-settings/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQrFile(null);
      }
      toast.success("Payment settings saved");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="mt-6 text-purple-200/70">Loading…</div>;

  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="glass-strong rounded-3xl p-6">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Shield className="w-4 h-4" /> Payment Settings</h2>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-purple-200/70">UPI ID</span>
            <input data-testid="settings-upi" value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="name@bank" className="mt-1 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 font-mono text-white outline-none" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-purple-200/70">Payment Amount (₹)</span>
            <input data-testid="settings-amount" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-white outline-none" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-purple-200/70">Instructions</span>
            <textarea data-testid="settings-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={4} className="mt-1 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-white outline-none" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="settings-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enable manual UPI payments
          </label>
          <button data-testid="settings-save" disabled={busy} onClick={save} className="btn-cta w-full py-2.5 rounded-xl mt-2">{busy ? "Saving…" : "Save settings"}</button>
        </div>
      </div>

      <div className="glass rounded-3xl p-6">
        <h2 className="font-display text-xl font-bold">UPI QR Code</h2>
        <div className="mt-4 bg-white p-3 rounded-2xl inline-block">
          {settings.qr_photo_id ? (
            <img src={`${API}/payments/qr-image?t=${Date.now()}`} alt="UPI QR" className="w-56 h-56 object-contain" />
          ) : (
            <div className="w-56 h-56 flex items-center justify-center text-slate-400 text-xs">No QR uploaded</div>
          )}
        </div>
        <label className="mt-4 block">
          <span className="text-[11px] uppercase tracking-widest text-purple-200/70">Replace QR image</span>
          <input data-testid="settings-qr-file" type="file" accept="image/*" onChange={(e) => setQrFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-xs text-purple-200" />
        </label>
        {qrFile && <div className="text-[11px] text-amber-300 mt-1">New file selected — click "Save settings" to upload.</div>}
      </div>
    </div>
  );
}
